import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useTenantStore } from "../stores/useTenantStore";
import { tenantPreferencesApi } from "../services/tenantPreferencesApi";
import type {
  PreferencesResponse,
  PreferencesUpdateRequest,
  PreferenceHistoryEntry,
  FeedbackPattern,
} from "../types/tenantPreferences";
import type {
  AnalyzePreferencesRequest,
  PreferenceDiscoveryResponse,
} from "../types/preferenceDiscovery";

// Query key factory
export const preferencesKeys = {
  all: ["contentPreferences"] as const,
  detail: (website: string) =>
    [...preferencesKeys.all, "detail", website] as const,
  history: (website: string) =>
    [...preferencesKeys.all, "history", website] as const,
  context: (website: string) =>
    [...preferencesKeys.all, "context", website] as const,
  patterns: (website: string) =>
    [...preferencesKeys.all, "patterns", website] as const,
};

export function useContentPreferences(tenantId?: string) {
  const { currentTenant } = useTenantStore();
  const effectiveTenant = tenantId ?? currentTenant ?? "";
  const queryClient = useQueryClient();

  const query = useQuery<PreferencesResponse>({
    queryKey: preferencesKeys.detail(effectiveTenant),
    queryFn: () => tenantPreferencesApi.getPreferences(effectiveTenant),
    enabled: !!effectiveTenant,
    staleTime: 2 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      tenantPreferencesApi.updatePreferences(effectiveTenant, data as PreferencesUpdateRequest),
    onSuccess: (updated) => {
      queryClient.setQueryData(preferencesKeys.detail(effectiveTenant), updated);
    },
  });

  const rollbackMutation = useMutation({
    mutationFn: (version: number) =>
      tenantPreferencesApi.rollback(effectiveTenant, { target_version: version }),
    onSuccess: (updated) => {
      queryClient.setQueryData(preferencesKeys.detail(effectiveTenant), updated);
    },
  });

  // When called with explicit tenantId, return unified interface
  if (tenantId !== undefined) {
    return {
      ...query,
      preferences: query.data,
      updatePreferences: async (data: Record<string, unknown>) => {
        await updateMutation.mutateAsync(data);
      },
      fetchHistory: async () => {
        return tenantPreferencesApi.getHistory(effectiveTenant);
      },
      rollback: async (version: number) => {
        await rollbackMutation.mutateAsync(version);
      },
    };
  }

  return query;
}

export function useUpdateContentPreferences() {
  const { currentTenant } = useTenantStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PreferencesUpdateRequest) =>
      tenantPreferencesApi.updatePreferences(currentTenant!, body),
    onSuccess: () => {
      message.success("Content preferences saved");
      queryClient.invalidateQueries({ queryKey: preferencesKeys.all });
    },
    onError: (error: Error) => {
      message.error(`Failed to save preferences: ${error.message}`);
    },
  });
}

export function usePreferencesHistory() {
  const { currentTenant } = useTenantStore();

  return useQuery<PreferenceHistoryEntry[]>({
    queryKey: preferencesKeys.history(currentTenant ?? ""),
    queryFn: () => tenantPreferencesApi.getHistory(currentTenant!),
    enabled: !!currentTenant,
  });
}

export function useFeedbackPatterns() {
  const { currentTenant } = useTenantStore();

  return useQuery<FeedbackPattern[]>({
    queryKey: preferencesKeys.patterns(currentTenant ?? ""),
    queryFn: () => tenantPreferencesApi.listPatterns(currentTenant!),
    enabled: !!currentTenant,
  });
}

export function useTogglePattern() {
  const { currentTenant } = useTenantStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patternId,
      isActive,
    }: {
      patternId: string;
      isActive: boolean;
    }) =>
      tenantPreferencesApi.togglePattern(currentTenant!, patternId, isActive),
    onSuccess: () => {
      message.success("Pattern updated");
      queryClient.invalidateQueries({ queryKey: preferencesKeys.patterns(currentTenant ?? "") });
    },
    onError: (error: Error) => {
      message.error(`Failed to update pattern: ${error.message}`);
    },
  });
}

export function useAnalyzePreferences() {
  const { currentTenant } = useTenantStore();

  return useMutation<PreferenceDiscoveryResponse, Error, AnalyzePreferencesRequest>({
    mutationFn: (body: AnalyzePreferencesRequest = {}) =>
      tenantPreferencesApi.analyzePreferences(currentTenant!, body),
    onSuccess: (data) => {
      message.success(
        `Analysis complete — ${data.posts_analyzed} posts analyzed`,
      );
    },
    onError: (error: Error) => {
      message.error(`Analysis failed: ${error.message}`);
    },
  });
}

export function useRollbackPreferences() {
  const { currentTenant } = useTenantStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (targetVersion: number) =>
      tenantPreferencesApi.rollback(currentTenant!, {
        target_version: targetVersion,
      }),
    onSuccess: (data) => {
      message.success(`Rolled back. New version: v${data.version}`);
      queryClient.invalidateQueries({ queryKey: preferencesKeys.all });
    },
    onError: (error: Error) => {
      message.error(`Rollback failed: ${error.message}`);
    },
  });
}
